import clsx from 'clsx';
import { Popover, Transition } from '@headlessui/react';

//type
import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';
import type { ComponentPropsWithoutRef } from 'react';

//components
import Icon from '@/components/base/Icon';

type RestrictedGroupType = Pick<SpGroupType, 'id' | 'name'>;

type GroupItemProps = ComponentPropsWithoutRef<'div'> & { group: RestrictedGroupType };
function GroupItem({ group, ...restProps }: GroupItemProps) {
    return (
        <div
            className="flex items-center gap-1.5 rounded-full bg-icoBlue-100 px-2 py-1 text-xxs text-icoBlue dark:bg-icoBlue/20"
            {...restProps}>
            <Icon icon="group" className="text-xs" />
            <span className={clsx('block max-w-[100px] overflow-hidden')}>
                <span className="block truncate text-xs">{group?.name}</span>
            </span>
        </div>
    );
}

type PopoverGroupProps = Pick<GroupListProps, 'groups'>;
function PopoverGroup({ groups }: PopoverGroupProps) {
    return (
        <Popover className="relative">
            <Popover.Button
                data-testid="popover-group-button"
                className="flex h-[22px] w-7 items-center justify-center rounded-xl bg-icoBlue-100 px-2 text-xxs !text-icoBlue dark:bg-icoBlue/20">
                <Icon icon="plus" />
                {groups.length}
            </Popover.Button>

            <Transition
                enter="transition duration-100 ease-out"
                enterFrom="scale-95 opacity-0"
                enterTo="scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="scale-100 opacity-100"
                leaveTo="scale-95 opacity-0">
                <Popover.Panel className="absolute -top-4 left-9 z-20 mt-1 -translate-y-1/2 rounded-m bg-white p-1 shadow-icoM">
                    <div className="scrollbar flex max-h-40 w-52 flex-wrap gap-1.5 overflow-y-auto overflow-x-hidden p-4">
                        {groups?.map((group) => (
                            <GroupItem
                                group={group}
                                key={group.id}
                                data-testid={`group-popover-${group.id}`}
                            />
                        ))}
                    </div>
                </Popover.Panel>
            </Transition>
        </Popover>
    );
}

type GroupListProps = {
    groups: RestrictedGroupType[];
    maxDisplayed?: number;
    hasRoundedBottom?: boolean;
};
export function GroupList({ groups, maxDisplayed = 3, hasRoundedBottom = true }: GroupListProps) {
    if (!groups?.length) {
        return null;
    }
    const displayed = groups?.slice(0, maxDisplayed);
    const inPopover = groups?.slice(maxDisplayed);

    return (
        <div
            className={clsx(
                'flex w-full flex-wrap items-center gap-1.5 border-t border-icoGray-300 py-3 pl-[105px] pr-8 dark:border-icoDarkMode-beton',
                {
                    'rounded-b': hasRoundedBottom
                }
            )}>
            {displayed?.map((group) => (
                <GroupItem group={group} key={group.id} data-testid={`group-${group.id}`} />
            ))}
            {inPopover?.length ? <PopoverGroup groups={inPopover} /> : null}
        </div>
    );
}
